import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { api, clearTokens, getAccess, setTokens } from "@/lib/api";
import { clientKind, deviceFingerprint, deviceName } from "@/lib/deviceIdentity";
import type { Me, TokenPair } from "@/types/types";

interface AuthCtx {
  user: Me | null;
  loading: boolean;
  login: (username: string, password: string, captchaToken?: string) => Promise<void>;
  register: (username: string, password: string, invite?: string, email?: string, captchaToken?: string) => Promise<void>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const bootstrap = useCallback(async () => {
    if (!getAccess()) { setLoading(false); return; }
    try { setUser(await api<Me>("/api/auth/me")); } catch { clearTokens(); }
    setLoading(false);
  }, []);

  useEffect(() => { void bootstrap(); }, [bootstrap]);

  const login = useCallback(async (username: string, password: string, captchaToken?: string) => {
    const p = await api<TokenPair>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({
        username, password,
        device_fingerprint: deviceFingerprint(),
        device_name: deviceName(),
        client: clientKind(),
        captcha_token: captchaToken,
      }),
    });
    setTokens(p);
    setUser(await api<Me>("/api/auth/me"));
  }, []);

  const register = useCallback(async (username: string, password: string, invite?: string, email?: string, captchaToken?: string) => {
    await api<Me>("/api/auth/register", {
      method: "POST",
      body: JSON.stringify({
        username, password, invite_code: invite, email,
        captcha_token: captchaToken,
      }),
    });
    await login(username, password);
  }, [login]);

  const logout = useCallback(async () => {
    const rt = localStorage.getItem("sl.refresh");
    if (rt) { try { await api("/api/auth/logout", { method: "POST", body: JSON.stringify({ refresh_token: rt }) }); } catch {} }
    clearTokens();
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, loading, login, register, logout }), [user, loading, login, register, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
